import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import { useFrontContext } from '@/features/FrontPlugin/provider/frontContext';
import PluginFeedbackPreview from '@/features/FrontPlugin/workspace/PluginFeedbackPreview';
import useColor from '@/hooks/domain/useColor';
import { useOnClickOutside } from '@/hooks/pure/useOnClickOutside';
import service from '@/services/index';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import type { FeedbackSentiment } from '@/types/feedbackConst';
import { DATE_FORMAT,getTimezoneDayjs } from '@/utils/time';

type FrontSentence = {
  primary: boolean;
  index: number;
  offset: number;
  sentiment: FeedbackSentiment;
  text: string;
};

export type FrontSentimentData = {
  date: string;
  link: string;
  sentences: FrontSentence[];
};

const SENTIMENT_WRAPPER_WIDTH = 32;

const PluginFeedbackChart = () => {
  const color = useColor();
  const timeout = useRef<NodeJS.Timer | null>(null);
  const [previewOpenMessageId, setPreviewOpenMessageId] = useState<
    string | null
  >(null);
  const [refIndex, setRefIndex] = useState<number | null>(null);
  const context = useFrontContext();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);

  const convId = useMemo(() => {
    if (!context) return;
    if (context.type !== 'singleConversation') return;
    return context.conversation?.id;
  }, [context]);

  const { data: response } = useQuery({
    queryKey: ['conv_id', { alias: currWorkspace?.alias }, convId],
    queryFn: () => {
      if (!convId) {
        throw new Error('Conversation id is required');
      }
      return service.front.fetchFrontSentiments(convId);
    },
    gcTime: 1000 * 60 * 5,
    enabled: !!convId,
  });

  const sentenses: { [key: string]: FrontSentimentData } = useMemo(() => {
    if (!response) return {};
    const s: { [key: string]: FrontSentimentData } = response.sentences;
    const sortedSentencesArray = Object.entries(s)
      .sort(([, a], [, b]) =>
        getTimezoneDayjs(a.date).diff(getTimezoneDayjs(b.date)),
      )
      .map(([key, value]) => ({ [key]: value }));

    const sortedSentences = Object.assign({}, ...sortedSentencesArray);
    return sortedSentences || {};
  }, [response]);

  const refsArray = useRef<HTMLDivElement[]>([]);

  const setRef = (index: number, element: HTMLDivElement | null) => {
    if (!element) return;
    refsArray.current[index] = element;
  };

  const handleMouseEnter = (key: string, index: number) => {
    timeout.current = setTimeout(() => {
      setPreviewOpenMessageId(key);
      setRefIndex(index);
    }, 300);
  };

  const handleMouseLeave = () => {
    setPreviewOpenMessageId(null);
    setRefIndex(null);
    if (!timeout.current) return;
    clearTimeout(timeout.current);
  };

  const divRef = useRef(null);
  useOnClickOutside(divRef, handleMouseLeave);

  const handleMouseClick = (key: string) => {
    if (!context) return;
    context.openUrl(`open/${key}`);
  };

  return (
    <StyledChart g={10} ref={divRef}>
      {Object.keys(sentenses).map((key, index) => {
        const data = sentenses[key];
        if (!data) return;
        const sentiment =
          data.sentences.find((d) => d.primary)?.sentiment ||
          data.sentences[0]?.sentiment ||
          2;
        return (
          <Flex
            a="center"
            g={10}
            className="sentiment-item"
            onMouseEnter={() => handleMouseEnter(key, index)}
            // onMouseLeave={handleMouseLeave}
            onClick={() => handleMouseClick(key)}
            key={index}
          >
            <CenterFlex className="sentiment-wrapper">
              <div
                key={index}
                className="sentiment"
                onClick={() => {}}
                style={{
                  backgroundColor: color.sentiment[sentiment],
                  aspectRatio: 1,
                  width: 12,
                }}
              ></div>
            </CenterFlex>

            <Flex
              className="time-container"
              a="center"
              j="space-between"
              f={1}
              ref={(element) => setRef(index, element)}
            >
              <Typo type="label2" color={color.gray700} weight="m">
                {getTimezoneDayjs(data.date).format(DATE_FORMAT.readable)}
              </Typo>
              <Typo type="label3" color={color.gray500}>
                {getTimezoneDayjs(data.date).format(DATE_FORMAT.timeOnly)}
              </Typo>
            </Flex>
          </Flex>
        );
      })}

      <div className="vertical-axis-wrapper">
        <div className="vertical-axis"></div>
      </div>

      {previewOpenMessageId !== null &&
        refIndex !== null &&
        sentenses[previewOpenMessageId] && (
          <>
            <PluginFeedbackPreview
              element={refsArray.current[refIndex]}
              data={sentenses[previewOpenMessageId]}
            />
          </>
        )}
    </StyledChart>
  );
};

export default PluginFeedbackChart;

const StyledChart = styled(VFlex)`
  position: relative;
  .sentiment-item {
    border-radius: 6px;
    cursor: pointer;
    position: relative;

    &:hover {
      background-color: ${(props) => props.theme.colors.gray200};
    }

    .sentiment-wrapper {
      width: ${SENTIMENT_WRAPPER_WIDTH}px;
      height: ${SENTIMENT_WRAPPER_WIDTH}px;

      .sentiment {
        border-radius: 24px;
        cursor: pointer;
      }
    }

    .time-container {
      flex: 1;
      padding: 0 10px;
    }
  }

  .vertical-axis-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${SENTIMENT_WRAPPER_WIDTH}px;
    padding: ${SENTIMENT_WRAPPER_WIDTH / 2}px 0;
    z-index: -1;

    .vertical-axis {
      height: 100%;
      width: 1px;
      margin: 0 auto;
      background-color: ${(props) => props.theme.colors.gray400};
    }
  }
`;
